import React, { useState } from 'react';
import {
  useCourtAdministrativeUnitAssignments,
  useCourts,
  useFederalStates,
} from '../../Aemter/hooks';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Error, Policy } from '@mui/icons-material';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { useGetLandRegisterAccesses } from '../../hooks/reactQuerys/FederalStateLoginQueries';
import { CustomeTextField } from '../../generic/CustomeTextField';
import {
  ISearchOptionLandRegister,
  searchOptionLandRegisterSchema,
} from '../../Interfaces/Helper/ISearchOptionLandRegister';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { QueryLandRegisterDownload } from './Childs/QueryLandRegisterDownload';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { useTriggerLandRegisterDownload } from '../../hooks/reactQuerys/LandMarkingQueries';
import { IDocument } from '../../Interfaces/IDocument';
import { useAlert } from '../../context/AlertContext';
import { IRealEstateLandRegisterEntryQuery } from '../../Interfaces/IRealEstateLandRegisterEntryQuery';
import { RealEstateLandRegisterEntryQueriesTable } from '../../RealEstate/RealEstateLandRegisterEntryQueriesTable';

interface IProps {
  realEstateObject: ILegalTransactionRelationshipRealEstate;
  setRealEstateObject: Function;
  realEstateLandRegisterEntryQueries: IRealEstateLandRegisterEntryQuery[];
  refetchRealEstateLandRegisterEntryQueries: Function;
  //
  legalTransaction: ILegalTransactionFullObject;
  /**
   * Trennt die beiden fachlichen Schritte:
   *  - 'owner'  → nur Eigentümerrecherche (Blatt/Eigentümer ermitteln),
   *  - 'abruf'  → nur Abrufe (Markentabelle/Aktualitätsnachweis/Grundbuchauszug).
   * Ohne mode (Default) werden beide Sektionen gezeigt (Altverhalten).
   */
  mode?: 'owner' | 'abruf';
}

const DOCUMENT_TYPES = {
  GB: { id: 20, label: 'Grundbuchauszug' },
  MT: { id: 21, label: 'Markentabelle' },
  AN: { id: 22, label: 'Aktualitätsnachweis' },
} as const;

export const QueryLandRegisterDialog: React.FC<IProps> = ({
  realEstateObject,
  setRealEstateObject,
  realEstateLandRegisterEntryQueries,
  refetchRealEstateLandRegisterEntryQueries,
  //
  legalTransaction,
  mode,
}) => {
  const { showAlert } = useAlert();

  const [isOpen, setIsOpen] = useState(false);
  //
  const { federalStates, isLoadingFederalStates } = useFederalStates();
  // Nur dem aktuellen Benutzer zugeordnete (persönliche) Zugänge
  const { landRegisterAccesses, isLoadingLandRegisterAccesses } =
    useGetLandRegisterAccesses();
  const federalStateLogins = landRegisterAccesses;
  const isFederalStateLogins = isLoadingLandRegisterAccesses;
  const { courts, isLoadingCourts } = useCourts();
  const {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
  } = useCourtAdministrativeUnitAssignments();
  const { users, isLoadingUsers } = useGetUsers();
  //
  const { downloadLandRegister, isDownloadingLandMarkings } =
    useTriggerLandRegisterDownload();
  const [downloadedDocument, setDownloadedDocument] =
    useState<IDocument | null>(null);
  //
  const [search, setSearch] = useState<ISearchOptionLandRegister>(
    searchOptionLandRegisterSchema.parse({})
  );
  const [isOpenDownloadWarning, setIsOpenDownloadWarning] = useState(false);
  // Passwort wird erst nach „Beziehen" + Kostenwarnung abgefragt (nicht gespeichert).
  const [pwPromptOpen, setPwPromptOpen] = useState(false);
  const [pwValue, setPwValue] = useState('');

  const AVAILABLE_TYPES = Object.keys(
    DOCUMENT_TYPES
  ) as (keyof typeof DOCUMENT_TYPES)[];
  const EXISTING_DOC_TYPES = Object.values(DOCUMENT_TYPES).map(t => t.id);

  //
  const currentLandParcelEntry = realEstateObject.LandParcelEntryArray.find(
    landParcel =>
      landParcel.idRealEstateLandRegisterEntry ===
      search.idRealEstateLandRegisterEntry
  );
  const currentCourt = courts?.find(
    court => court.idCourt === currentLandParcelEntry?.idCourt
  );
  const currentFedearlState = federalStates?.find(
    federalState => federalState.idFederalState === currentCourt?.idFederalState
  );
  const currentAdminUnit = courtAdministrativeUnitAssignments?.find(
    adminUnit =>
      adminUnit.idAdministrativeUnit ===
      currentLandParcelEntry?.idAdministrativeUnit
  );

  const toggle = (t: string) =>
    setSearch({
      ...search,
      SelectedDownload: search.SelectedDownload.includes(t)
        ? search.SelectedDownload.filter(x => x !== t)
        : [...search.SelectedDownload, t],
    });

  const checkNone = (object: any, attr: string) => {
    if (object && attr in object && object[attr]) {
      return String(object[attr]);
    } else {
      return 'k.A.';
    }
  };

  const handleOpen = () => {
    const idRealEstateLandRegisterEntry = null;
    const idFederalState = null;

    // Erstelle Suchstring
    let searchCaseNumber = legalTransaction.Title;

    if (legalTransaction.TransactionNumberArray.length > 0) {
      searchCaseNumber =
        legalTransaction.TransactionNumberArray[0].TransactionNumberEntry;
    }

    if (realEstateObject.LandParcelEntryArray.length === 1) {
      const landParcel = realEstateObject.LandParcelEntryArray[0];
      // Kopiere von LandParcelEntry
      const localSearchObject = searchOptionLandRegisterSchema.parse({
        ...landParcel,
        SearchCaseNumber: searchCaseNumber,
        StreetSearch:
          realEstateObject.Street && realEstateObject.StreetNr
            ? `${realEstateObject.Street} ${realEstateObject.StreetNr}`
            : realEstateObject.Street
              ? `${realEstateObject.Street}`
              : realEstateObject.StreetNr
                ? `${realEstateObject.StreetNr}`
                : '',
      });

      // Bundesland, wenn Court gefunden wurde
      const testObject = courts?.find(
        court => court.idCourt === landParcel.idCourt
      );
      if (testObject) {
        localSearchObject.idFederalState = testObject?.idFederalState;
        // Prüfe, ob Login bereits gefunden werden kann
        const testLogins = federalStateLogins
          ?.filter(federalStateLogin => federalStateLogin.isFirstStage == false)
          .filter(login => login.idFederalState === testObject?.idFederalState);
        if (testLogins?.length === 1) {
          localSearchObject.idFederalStateLogin =
            testLogins[0].idFederalStateLogin;
        }
      }
      setSearch(localSearchObject);
    } else {
      setSearch(
        searchOptionLandRegisterSchema.parse({
          SearchCaseNumber: searchCaseNumber,
        })
      );
    }

    setIsOpen(true);
  };

  const onDownload = async (password: string) => {
    setPwPromptOpen(false);
    try {
      const uploadObject = searchOptionLandRegisterSchema.parse({
        ...search,
        Password: password,
      });
      await downloadLandRegister(uploadObject);
      refetchRealEstateLandRegisterEntryQueries();
      /*
      setRealEstateObject({
        ...realEstateObject,
        LandParcelEntryArray: realEstateObject.LandParcelEntryArray.map(
          landParcel => {
            if (
              landParcel.idRealEstateLandRegisterEntry !==
              search.idRealEstateLandRegisterEntry
            ) {
              return landParcel;
            } else {
              return {
                ...landParcel,
                idDocument: document.idDocument,
              } as ILandParcelEntry;
            }
          }
        ),
      });
      */
      showAlert({
        severity: 'success',
        text: 'Grundbuchauszug erfolgreich bezogen!',
      });
      setIsOpen(false);
    } catch {
      showAlert({
        severity: 'error',
        text: 'Der Grundbuchauszug konnte nicht bezogen werden! Bitte überprüfen Sie Ihre Anmeldeinformationen und die Verfügbarkeit des Grundbuchs',
      });
    }
  };

  if (!isOpen) {
    return (
      <IconButton onClick={handleOpen}>
        <Policy />
      </IconButton>
    );
  }

  if (
    isLoadingFederalStates ||
    isFederalStateLogins ||
    isLoadingCourts ||
    isLoadingCourtAdministrativeUnitAssignments ||
    isLoadingUsers
  ) {
    return <CircularProgress />;
  }

  if (
    !federalStates ||
    !federalStateLogins ||
    !courts ||
    !courtAdministrativeUnitAssignments ||
    !users
  ) {
    return (
      <IconButton disabled title='Die Gerichte konnten nicht bezogen werden!'>
        <Error />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton>
        <Policy />
      </IconButton>

      {isDownloadingLandMarkings && (
        <Dialog open>
          <DialogContent>
            <Typography>
              Der Grundbuchauszug wird bezogen. <br />
              Dies kann mehrere Minuten dauern, bitte haben Sie geduld!
            </Typography>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={isOpenDownloadWarning}
        onClose={() => setIsOpenDownloadWarning(false)}
      >
        <DialogTitle>Download bestätigen</DialogTitle>
        <DialogContent>
          <Alert severity='warning'>
            <AlertTitle>Achtung: Kostenpflichtig!</AlertTitle>
            Für jeden Bezug eines Grundbucheintrags fallen kosten an.
            <br />
            Sind Sie sich sicher, dass Sie den Grundbucheintrag beziehen wollen?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => {
              setIsOpenDownloadWarning(false);
              setPwValue('');
              setPwPromptOpen(true);
            }}
          >
            Beziehen
          </Button>
          <Button
            variant='contained'
            onClick={() => setIsOpenDownloadWarning(false)}
          >
            Abbruch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passwort wird erst nach Bestätigung der Kostenwarnung abgefragt */}
      <Dialog
        open={pwPromptOpen}
        onClose={() => setPwPromptOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Passwort des Zugangs</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Bitte geben Sie das Passwort Ihres ausgewählten Zugangs ein. Es wird
            nur für diesen Abruf verwendet und nicht gespeichert.
          </Typography>
          <TextField
            type='password'
            label='Passwort'
            fullWidth
            autoFocus
            value={pwValue}
            onChange={e => setPwValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && pwValue) onDownload(pwValue);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setPwPromptOpen(false)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={!pwValue}
            onClick={() => onDownload(pwValue)}
          >
            Beziehen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open onClose={() => setIsOpen(false)} maxWidth='lg' fullWidth>
        <DialogTitle>
          {mode === 'owner'
            ? 'Eigentümerrecherche (Grundbuch)'
            : mode === 'abruf'
              ? 'Grundbuch-Abrufe'
              : 'Grundbuchauszug'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={8}>
              <CustomeTextField
                label='Grundbucheintrag'
                attr='idRealEstateLandRegisterEntry'
                object={search}
                setObject={setSearch}
                type='select'
                null={search.idRealEstateLandRegisterEntry === null}
              >
                {realEstateObject.LandParcelEntryArray.map(landParcelEntry => (
                  <MenuItem
                    key={`select-landparcelentry-${landParcelEntry.idRealEstateLandRegisterEntry}`}
                    value={landParcelEntry.idRealEstateLandRegisterEntry}
                  >
                    {checkNone(currentFedearlState, 'FederalState')} //{' '}
                    {checkNone(currentCourt, 'Court')} //{' '}
                    {checkNone(currentAdminUnit, 'AdministrativeUnit')} //{' '}
                    {checkNone(landParcelEntry, 'Parcel')} //{' '}
                    {checkNone(landParcelEntry, 'LandParcel')} //{' '}
                    {checkNone(landParcelEntry, 'SheetLandRegister')}
                  </MenuItem>
                ))}
              </CustomeTextField>
            </Grid>
            <Grid item xs={4}>
              <CustomeTextField
                label='Aktenzeichen Suche'
                attr='SearchCaseNumber'
                object={search}
                setObject={(updatedSearch: ISearchOptionLandRegister) => {
                  setSearch({
                    ...updatedSearch,
                  });
                }}
                type='string'
              />
            </Grid>
          </Grid>

          <Collapse in={search.idRealEstateLandRegisterEntry !== null}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Bundesland'
                  attr='idFederalState'
                  object={search}
                  setObject={(updatedSearch: ISearchOptionLandRegister) => {
                    // Prüfe, ob Login bereits gefunden werden kann
                    let idFederalStatLogin: number | null = null;
                    const testLogins = federalStateLogins
                      ?.filter(
                        federalStateLogin =>
                          federalStateLogin.isFirstStage == false
                      )
                      .filter(
                        login =>
                          login.idFederalState === updatedSearch?.idFederalState
                      );
                    if (testLogins?.length === 1) {
                      idFederalStatLogin = testLogins[0].idFederalStateLogin;
                    }

                    if (
                      courts.find(
                        court =>
                          court.idCourt ===
                          realEstateObject.LandParcelEntryArray.find(
                            landParcelEntry =>
                              landParcelEntry.idRealEstateLandRegisterEntry ===
                              search.idRealEstateLandRegisterEntry
                          )?.idCourt
                      ) !== undefined
                    ) {
                      const orgObject =
                        realEstateObject.LandParcelEntryArray.find(
                          landParcelEntry =>
                            landParcelEntry.idRealEstateLandRegisterEntry ===
                            search.idRealEstateLandRegisterEntry
                        );
                      if (orgObject) {
                        setSearch({
                          ...orgObject,
                          Password: search.Password,
                          idFederalStateLogin: idFederalStatLogin,
                          idFederalState: updatedSearch.idFederalState,
                          SearchCaseNumber: updatedSearch.SearchCaseNumber,
                          SearchTerm: '',
                          StreetSearch: '',
                          SelectedDownload: ['GB'],
                        });
                      } else {
                        setSearch({
                          ...updatedSearch,
                          idFederalStateLogin: idFederalStatLogin,
                          idCourt: null,
                          idAdministrativeUnit: null,
                        });
                      }
                    } else {
                      setSearch({
                        ...updatedSearch,
                        idFederalStateLogin: idFederalStatLogin,
                        idCourt: null,
                        idAdministrativeUnit: null,
                      });
                    }
                  }}
                  type='select'
                  null={search.idFederalState === null}
                >
                  {federalStates.map(federalState => (
                    <MenuItem
                      key={`select-federalState-${federalState.idFederalState}`}
                      value={federalState.idFederalState}
                    >
                      {federalState.FederalState}
                    </MenuItem>
                  ))}
                </CustomeTextField>
              </Grid>
              <Grid item xs={6}>
                <CustomeTextField
                  label='Zugang'
                  attr='idFederalStateLogin'
                  object={search}
                  setObject={(updatedSearch: ISearchOptionLandRegister) => {
                    setSearch({
                      ...updatedSearch,
                    });
                  }}
                  type='select'
                  null={search.idFederalStateLogin === null}
                >
                  {federalStateLogins
                    .filter(
                      federalStateLogin =>
                        federalStateLogin.isFirstStage == false
                    )
                    .filter(
                      federalStateLogin =>
                        federalStateLogin.idFederalState ===
                        search.idFederalState
                    )
                    .map(federalStateLogin => (
                      <MenuItem
                        key={`select-federalStateLogin-${federalStateLogin.idFederalStateLogin}`}
                        value={federalStateLogin.idFederalStateLogin}
                      >
                        {
                          users.find(
                            user =>
                              user.idUser === federalStateLogin.Notary_idUser
                          )?.LastName
                        }{' '}
                        // {federalStateLogin.Username}
                      </MenuItem>
                    ))}
                </CustomeTextField>
              </Grid>
            </Grid>
          </Collapse>

          {/* Eigentümerrecherche erfolgt vorgelagert im eigenen Dialog -> hier raus */}
          <Collapse in={search.idFederalStateLogin !== null}>
            <>
              <QueryLandRegisterDownload
                search={search}
                setSearch={setSearch}
                adminUnits={courtAdministrativeUnitAssignments}
                courts={courts}
              />
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography
                    variant='subtitle1'
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Abrufe beziehen
                  </Typography>
                  {AVAILABLE_TYPES.map(t => (
                    <FormControlLabel
                      key={t}
                      control={
                        <Checkbox
                          checked={search.SelectedDownload.includes(t)}
                          onChange={() => toggle(t)}
                        />
                      }
                      label={DOCUMENT_TYPES[t].label}
                    />
                  ))}
                </CardContent>
              </Card>
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography
                    variant='subtitle1'
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Bereits vorhandene Abrufe
                  </Typography>
                  <RealEstateLandRegisterEntryQueriesTable
                    realEstateLandRegisterEntryQueries={
                      realEstateLandRegisterEntryQueries
                    }
                  />
                </CardContent>
              </Card>
            </>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setIsOpen(false)}>
            Abbruch
          </Button>
          {mode !== 'owner' && (
            <Button
              variant='contained'
              onClick={() => setIsOpenDownloadWarning(true)}
              disabled={
                search.SheetLandRegister === null ||
                search.SheetLandRegister === '' ||
                search.SelectedDownload.length === 0
              }
            >
              Beziehen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
